import type { Palette } from "./types";
import { BLOCK_COLOR, DANGER_COLOR } from "./constants";

// Theming layer. The whole UI reads its colors from :root CSS custom
// properties (same approach as the prototype), so applying a palette is just a
// matter of writing those variables. Both customer and admin pages call
// applyPalette so a config change themes the live page.

export const PALETTE_TOKENS: Array<{ key: keyof Palette; label: string; cssVar: string }> = [
  { key: "accent", label: "Accent", cssVar: "--accent" },
  { key: "accentDark", label: "Accent (dark)", cssVar: "--accent-d" },
  { key: "accentSoft", label: "Accent (soft)", cssVar: "--accent-soft" },
  { key: "bg", label: "Background", cssVar: "--bg" },
  { key: "surface", label: "Surface", cssVar: "--surface" },
  { key: "ink", label: "Text", cssVar: "--ink" },
  { key: "muted", label: "Muted text", cssVar: "--muted" },
  { key: "line", label: "Lines", cssVar: "--line" },
];

export function applyPalette(p: Palette, root: HTMLElement = document.documentElement): void {
  for (const { key, cssVar } of PALETTE_TOKENS) {
    root.style.setProperty(cssVar, p[key]);
  }
  // Fixed tokens not part of the editable palette.
  root.style.setProperty("--block", BLOCK_COLOR);
  root.style.setProperty("--danger", DANGER_COLOR);
}

// ---- WCAG contrast (used by the admin readability guardrail) ----

function srgbToLinear(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

function hexToRgb(hex: string): [number, number, number] | null {
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

export function isValidHex(hex: string): boolean {
  return hexToRgb(hex) !== null;
}

function luminance(hex: string): number | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map(srgbToLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// WCAG contrast ratio between two hex colors, or null if either is invalid.
export function contrastRatio(fg: string, bg: string): number | null {
  const lf = luminance(fg);
  const lb = luminance(bg);
  if (lf === null || lb === null) return null;
  const lighter = Math.max(lf, lb);
  const darker = Math.min(lf, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

export const AA_NORMAL = 4.5;
export const AA_LARGE = 3.0;

export interface ContrastCheck {
  label: string;
  fg: string;
  bg: string;
  ratio: number | null;
  threshold: number;
  passes: boolean;
}

// The two relationships that matter most for readability:
//   - accent buttons carry white text
//   - body text (ink) sits on the background
export function paletteContrastChecks(p: Palette): ContrastCheck[] {
  const make = (label: string, fg: string, bg: string, threshold: number): ContrastCheck => {
    const ratio = contrastRatio(fg, bg);
    return { label, fg, bg, ratio, threshold, passes: ratio !== null && ratio >= threshold };
  };
  return [
    make("White text on accent buttons", "#FFFFFF", p.accent, AA_NORMAL),
    make("Body text on background", p.ink, p.bg, AA_NORMAL),
    make("Body text on surface", p.ink, p.surface, AA_NORMAL),
    make("Accent text on surface", p.accent, p.surface, AA_LARGE),
    make("Muted text on background", p.muted, p.bg, AA_LARGE),
  ];
}
